import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { Release, ReleaseStatus } from '@/types'
import type { ReleasesState } from './state'
import { useProjectsStore } from '@/views/Projects/store'

export interface CreateReleaseData {
  projectId: string
  version: string
  name: string
  description?: string
  requirementIds?: string[]
  plannedDate?: Date
  releaseNotes?: string
}

export interface UpdateReleaseData {
  version?: string
  name?: string
  description?: string
  status?: ReleaseStatus
  requirementIds?: string[]
  plannedDate?: Date
  releaseNotes?: string
}

export const createActions = (state: ReleasesState) => {
  const { logCreate, logUpdate, logDelete, detectChanges } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  const mapReleaseDoc = (docSnap: { id: string; data: () => Record<string, unknown> }): Release => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      tenantId: data.tenantId,
      projectId: data.projectId,
      version: data.version,
      name: data.name,
      description: data.description,
      status: data.status,
      requirementIds: (data.requirementIds as string[]) || [],
      plannedDate: (data.plannedDate as { toDate: () => Date })?.toDate(),
      deployedAt: (data.deployedAt as { toDate: () => Date })?.toDate(),
      releaseNotes: data.releaseNotes,
      createdBy: (data.createdBy as string) || '',
      createdAt: (data.createdAt as { toDate: () => Date })?.toDate() || new Date(),
      updatedAt: (data.updatedAt as { toDate: () => Date })?.toDate() || new Date(),
      updatedBy: (data.updatedBy as string) || '',
    } as Release
  }

  let globalUnsubscribe: Unsubscribe | null = null

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return
    state.isLoading = true
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []
    const q = query(collection(db, 'releases'), ...baseConstraints, orderBy('createdAt', 'desc'))
    globalUnsubscribe = onSnapshot(q, (snapshot) => {
      let releases = snapshot.docs.map(mapReleaseDoc)
      // Client-side filter for operators
      const authStore = useAuthStore()
      if (authStore.user?.role === 'operator') {
        const projectIds = new Set(useProjectsStore().projects.map((p) => p.id))
        releases = releases.filter((r) => projectIds.has(r.projectId))
      }
      state.releases = releases
      state.isLoading = false
    }, (error) => {
      console.error('Releases subscription error:', error)
      state.isLoading = false
    })
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchReleases = async (): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []

      if (authStore.user?.role === 'operator') {
        const projectsStore = useProjectsStore()
        if (projectsStore.projects.length === 0) await projectsStore.fetchProjects()
        const projectIds = projectsStore.projects.map(p => p.id)
        if (projectIds.length === 0) {
          state.releases = []
          state.isLoading = false
          return
        }
        // Fetch in batches of 30
        const allReleases: Release[] = []
        for (let i = 0; i < projectIds.length; i += 30) {
          const batch = projectIds.slice(i, i + 30)
          const batchQ = query(
            collection(db, 'releases'),
            ...baseConstraints,
            where('projectId', 'in', batch),
            orderBy('createdAt', 'desc')
          )
          const snap = await getDocs(batchQ)
          allReleases.push(...snap.docs.map(mapReleaseDoc))
        }
        state.releases = allReleases.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        state.isLoading = false
        return
      }

      const q = query(collection(db, 'releases'), ...baseConstraints, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      state.releases = snapshot.docs.map(mapReleaseDoc)
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching releases'
      console.error('Error fetching releases:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchReleasesByProject = async (projectId: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      const q = query(
        collection(db, 'releases'),
        ...baseConstraints,
        where('projectId', '==', projectId),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)

      state.releases = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          tenantId: data.tenantId,
          projectId: data.projectId,
          version: data.version,
          name: data.name,
          description: data.description,
          status: data.status,
          requirementIds: data.requirementIds || [],
          plannedDate: data.plannedDate?.toDate(),
          deployedAt: data.deployedAt?.toDate(),
          releaseNotes: data.releaseNotes,
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          updatedBy: data.updatedBy || '',
        } as Release
      })
    } catch (error) {
      state.error = error instanceof Error ? error.message : 'Error fetching releases'
      console.error('Error fetching releases:', error)
    } finally {
      state.isLoading = false
    }
  }

  const fetchReleaseById = async (id: string): Promise<Release | null> => {
    try {
      const docRef = doc(db, 'releases', id)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        return null
      }

      const data = docSnap.data()
      return {
        id: docSnap.id,
        tenantId: data.tenantId,
        projectId: data.projectId,
        version: data.version,
        name: data.name,
        description: data.description,
        status: data.status,
        requirementIds: data.requirementIds || [],
        plannedDate: data.plannedDate?.toDate(),
        deployedAt: data.deployedAt?.toDate(),
        releaseNotes: data.releaseNotes,
        createdBy: data.createdBy || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || '',
      } as Release
    } catch (error) {
      console.error('Error fetching release:', error)
      return null
    }
  }

  const createRelease = async (data: CreateReleaseData): Promise<string> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''

    const releaseData = {
      ...getTenantData(),
      projectId: data.projectId,
      version: data.version,
      name: data.name,
      description: data.description || null,
      status: 'planned' as ReleaseStatus,
      requirementIds: data.requirementIds || [],
      plannedDate: data.plannedDate || null,
      releaseNotes: data.releaseNotes || null,
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    const docRef = await addDoc(collection(db, 'releases'), releaseData)

    const newRelease: Release = {
      id: docRef.id,
      projectId: data.projectId,
      version: data.version,
      name: data.name,
      description: data.description,
      status: 'planned',
      requirementIds: data.requirementIds || [],
      plannedDate: data.plannedDate,
      releaseNotes: data.releaseNotes,
      createdBy: userId,
      updatedBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    state.releases.unshift(newRelease)

    await logCreate('release', docRef.id, `${data.version} - ${data.name}`)

    // Notify admins and operators about new release
    await notificationsStore.notifyAdminsAndOperators(
      'release_created',
      'Nuevo release creado',
      `Se ha creado el release ${data.version}: "${data.name}"`,
      'release',
      docRef.id
    )

    return docRef.id
  }

  const updateRelease = async (id: string, data: UpdateReleaseData): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'releases', id)
    const existingRelease = state.releases.find((r) => r.id === id)

    const updateData: Record<string, unknown> = {
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    if (data.version !== undefined) updateData.version = data.version
    if (data.name !== undefined) updateData.name = data.name
    if (data.description !== undefined) updateData.description = data.description
    if (data.status !== undefined) updateData.status = data.status
    if (data.requirementIds !== undefined) updateData.requirementIds = data.requirementIds
    if (data.plannedDate !== undefined) updateData.plannedDate = data.plannedDate
    if (data.releaseNotes !== undefined) updateData.releaseNotes = data.releaseNotes

    await updateDoc(docRef, updateData)

    const index = state.releases.findIndex((r) => r.id === id)
    if (index !== -1) {
      state.releases[index] = {
        ...state.releases[index],
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingRelease) {
      const fieldsToTrack: (keyof Release)[] = ['status', 'version', 'name']
      const changes = detectChanges(
        existingRelease as unknown as Record<string, unknown>,
        data as Record<string, unknown>,
        fieldsToTrack as string[]
      )
      if (changes.length > 0) {
        await logUpdate('release', id, changes, `${existingRelease.version} - ${existingRelease.name}`)
      }
    }
  }

  const updateReleaseStatus = async (id: string, status: ReleaseStatus): Promise<void> => {
    const authStore = useAuthStore()
    const userId = authStore.user?.id || ''
    const docRef = doc(db, 'releases', id)
    const existingRelease = state.releases.find((r) => r.id === id)

    const updatePayload: Record<string, unknown> = {
      status,
      updatedBy: userId,
      updatedAt: serverTimestamp(),
    }

    // If deployed, set deployedAt timestamp
    if (status === 'deployed') {
      updatePayload.deployedAt = serverTimestamp()
    }

    await updateDoc(docRef, updatePayload)

    const index = state.releases.findIndex((r) => r.id === id)
    if (index !== -1) {
      state.releases[index] = {
        ...state.releases[index],
        status,
        deployedAt: status === 'deployed' ? new Date() : state.releases[index].deployedAt,
        updatedBy: userId,
        updatedAt: new Date(),
      }
    }

    if (existingRelease) {
      await logUpdate('release', id, [
        { field: 'status', oldValue: existingRelease.status, newValue: status },
      ], `${existingRelease.version} - ${existingRelease.name}`)
    }
  }

  const deleteRelease = async (id: string): Promise<void> => {
    const release = state.releases.find((r) => r.id === id)

    await deleteDoc(doc(db, 'releases', id))

    state.releases = state.releases.filter((r) => r.id !== id)

    if (release) {
      await logDelete('release', id, `${release.version} - ${release.name}`)
    }
  }

  const setFilters = (filters: Partial<ReleasesState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const getReleaseById = (id: string): Release | undefined => {
    return state.releases.find((r) => r.id === id)
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchReleases,
    fetchReleasesByProject,
    fetchReleaseById,
    createRelease,
    updateRelease,
    updateReleaseStatus,
    deleteRelease,
    setFilters,
    getReleaseById,
  }
}
